import {Action, Card, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetArticleQuery, usePatchArticleMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  logError,
  refreshDmsDatagrid,
  Section,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {AssignLocationDialog, handleWarehouseAclApiError} from '@dms/shared-aftersales';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

import {useArticleAllowedPermissions} from '../../../../hooks/useArticleAllowedPermissions';
import {getArticleStorageLocationPatchBody} from './utils/getArticleStorageLocationPatchBody';

const GRID_CODE = WAREHOUSE_GRID_CODES.warehouseArticleStorageLocationAssigned;
const ASSIGN_LOCATION_DIALOG_ID = 'article-detail-assign-location-dialog';

type LocationsProps = RequiredTestIdProps & {
  maximization?: EagGridMaximization;
};

export function Locations(props: LocationsProps) {
  const params = useWarehouseParams();

  const {article} = useGetArticleQuery(
    {articleId: params.articleId, warehouseId: params.warehouseId, isAclScoped: true},
    {selectFromResult: ({data}) => ({article: data})}
  );

  const [patchArticle] = usePatchArticleMutation();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {articleId: params.articleId}]),
    [params.articleId]
  );

  const handleAssignLocation = (storageLocationId: string) => {
    if (isNil(article)) {
      return logError('Cannot assign a storage location before the article is loaded');
    }

    const body = getArticleStorageLocationPatchBody(article, storageLocationId);

    if (isNil(body)) {
      return logError('Cannot assign a storage location to an article without a supplier');
    }

    patchArticle({
      articleId: params.articleId,
      warehouseId: params.warehouseId,
      body,
      isAclScoped: true,
    })
      .unwrap()
      .then(() => refreshDmsDatagrid(GRID_CODE))
      .catch(handleWarehouseAclApiError);
  };

  const handleOpenAssignLocationDialog = () => {
    openDialog(
      <AssignLocationDialog
        warehouseId={params.warehouseId}
        dialogId={ASSIGN_LOCATION_DIALOG_ID}
        onAssign={handleAssignLocation}
      />,
      {
        title: i18n.t('entity.warehouse.labels.assignStorageLocation'),
        id: ASSIGN_LOCATION_DIALOG_ID,
        size: 'large',
      }
    );
  };

  const actionCallback: ActionCallback = (callbackParams) =>
    catchUnhandledDataGridActions(callbackParams.actionKey);

  // A storage location can only be assigned via a full `patchArticle`, which requires a supplier.
  // Guard the action so supplier-less articles don't open a dialog that would silently fail.
  const hasSupplier = isNotNil(article?.supplierId);
  const {canEdit} = useArticleAllowedPermissions(article);

  const assignLocationTooltip = match({canEdit, hasSupplier})
    .with({canEdit: false}, () => i18n.t('general.labels.noPermission'))
    .with({hasSupplier: false}, () =>
      i18n.t('entity.warehouse.labels.assignStorageLocationSupplierRequired')
    )
    .otherwise(() => undefined);

  const cardActions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/link',
      title: i18n.t('entity.warehouse.labels.assignStorageLocation'),
      isDisabled: !canEdit || !hasSupplier,
      tooltip: assignLocationTooltip,
      onClick: handleOpenAssignLocationDialog,
      'data-testid': suffixTestId('assignLocation', props),
    },
  ];

  return (
    <Section isFullHeight>
      <Card title={i18n.t('entity.warehouse.labels.locations')} actions={cardActions} isFullHeight>
        <div css={{height: '100%'}}>
          <DmsDataGrid
            maximization={props.maximization}
            gridCode={GRID_CODE}
            queryModifier={queryModifier}
            actionCallback={actionCallback}
            data-testid={suffixTestId('locationsList', props)}
          />
        </div>
      </Card>
    </Section>
  );
}
